import { ApiAddress } from '@/services/apis'
import { ID } from '@/services/common.model'
import request from '@/services/request'
import { Account, Invitation, Member } from './member.model'

export async function getOrganizationMembers(id: ID, groupName?: string): Promise<Member.Info[]> {
  const members = (await request.get(ApiAddress.ORGANIZATION_MEMBERS, { params: { id } })) as Member.Info[]
  return filterMembersByGroupName(members, groupName)
}

export function updateMember(id: ID, mId: ID, body: Member.Update) {
  return request.put(ApiAddress.ORGANIZATION_MEMBER, body, { params: { id, mId } })
}

export function deleteMember(id: ID, mId: ID) {
  return request.delete(ApiAddress.ORGANIZATION_MEMBER, { params: { id, mId } })
}

export function resetMemberPassword(id: ID, mId: ID): Promise<Member.RestPasswordResult> {
  return request.put(ApiAddress.ORGANIZATION_MEMBER_RESET_PASSWORD, {}, { params: { id, mId } })
}

export function memberToOutsideMember(id: ID, mId: ID) {
  return request.post(ApiAddress.ORGANIZATION_MEMBER_OUTSIDE_MEMBER_GROUP, {}, { params: { id, mId } })
}

export function outsideMemberToMember(id: ID, mId: ID) {
  return request.post(ApiAddress.ORGANIZATION_MEMBER_MEMBER_GROUP, {}, { params: { id, mId } })
}

export async function getOrganizationMemberGroups(id: ID): Promise<Member.MemberGroup[]> {
  const groups = (await request.get(ApiAddress.ORGANIZATION_MEMBER_GROUPS, { params: { id } })) as Member.MemberGroup[]
  return groups.filter((group) => !defaultGroupNames.includes(group.name))
}

export function getOrganizationMemberGroup(id: ID, gId: ID): Promise<Member.MemberGroup> {
  return request.get(ApiAddress.ORGANIZATION_MEMBER_GROUP, { params: { id, gId } })
}

export function getGroupMembers(id: ID, gId: ID): Promise<Member.Info[]> {
  return request.get(ApiAddress.ORGANIZATION_GROUP_MEMBERS, { params: { id, gId } })
}

export function createOrganizationMemberGroup(id: ID, body: Member.MemberGroup) {
  return request.post(ApiAddress.ORGANIZATION_MEMBER_GROUPS, body, { params: { id } })
}

export function updateMemberGroup(id: ID, gId: ID, body: Member.MemberGroup) {
  return request.put(ApiAddress.ORGANIZATION_MEMBER_GROUP, body, { params: { id, gId } })
}

export function deleteMemberGroup(id: ID, gId: ID) {
  return request.delete(ApiAddress.ORGANIZATION_MEMBER_GROUP, { params: { id, gId } })
}

export function acceptInvitation(token: string, termsId: ID) {
  return request.put(ApiAddress.INVITATION_TOKEN, { termsId }, { params: { token } })
}

export function rejectInvitation(token: string) {
  return request.put(ApiAddress.INVITATION_REJECT, {}, { params: { token } })
}

export function getInvitationInfo(token: string): Promise<Invitation.Info> {
  return request.get(ApiAddress.INVITATION_TOKEN, { params: { token } })
}

export function createAccountByOrganization(id: ID, body: Account.Create) {
  return request.post(ApiAddress.ORGANIZATION_ACCOUNT, body, { params: { id } })
}

export function invitation(body: Member.Invitation): Promise<{ fullUrl: string }> {
  return request.post(ApiAddress.INVITATIONS, body)
}

export function deleteInvitation(id: ID) {
  return request.delete(ApiAddress.INVITATION, { params: { id } })
}

export async function getProjectMembers(
  projectId: ID,
): Promise<Member.ProjectMember[]> {
  const members = (await request.get(ApiAddress.PROJECT_MEMBERS, {
    params: { projectId }
  })) as Member.ProjectMember[]

  return members
}

export function updateProjectMember(projectId: ID, mId: ID, body) {
  return request.put(ApiAddress.PROJECT_MEMBER, body, { params: { mId, projectId } })
}

export function deleteProjectMember(projectId: ID, mId: ID) {
  return request.delete(ApiAddress.PROJECT_MEMBER, { params: { mId, projectId } })
}

export function leaveProject(projectId: ID) {
  return request.delete(ApiAddress.PROJECT_MEMBER_LEAVE, { params: { projectId } })
}

export function getInvitationPending(projectType, projectId: ID): Promise<Invitation.Pending[]> {
  return request.get(ApiAddress.INVITATIONS_PENDING, { params: { projectId, projectType } })
}

export function getInvitationFailed(projectType, projectId: ID): Promise<Invitation.Failed[]> {
  return request.get(ApiAddress.INVITATIONS_FAILED, { params: { projectId, projectType } })
}

export const projectInvitationDict = {
  projects: 'projectId',
  libraries: 'libId',
  'root-projects': 'rProjectId',
  'root-libraries': 'rLibId'
}

export const filterMembersByGroupName = (members: any[], groupName: string = '') => {
  if (!groupName) {
    return members
  }

  return members.filter((member) => member.groups.some((g) => g.name === groupName))
}

export const defaultGroupNames = ['OUTSIDE_MEMBERS', 'OUTSIDE_COLLABORATORS', 'PENDING_INVITATION', 'FAILED_INVITATION']
